import { createStore } from '@xstate/store';
import type { ColumnDefinition } from '../../types';

// ============================================
// Columns Store - Verwaltet die Columns (pro Board)
// ============================================

const PERSISTENCE_DEBOUNCE_MS = 500;

export const DEFAULT_COLUMNS: ColumnDefinition[] = [];

export interface ColumnsContext {
  columns: ColumnDefinition[];
}

function getLocalStorageKey(boardId: string): string {
  return `roadmap_2026_board_${boardId}_columns`;
}

function loadColumnsFromLocalStorage(storageKey: string): ColumnDefinition[] {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return [...DEFAULT_COLUMNS];

    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) {
      return [...DEFAULT_COLUMNS];
    }
    // Migration: string[] -> ColumnDefinition[]
    if (parsed.length > 0 && typeof parsed[0] === 'string') {
      return (parsed as string[]).map(name => ({ name, defaultCategoryIds: [] }));
    }
    return parsed as ColumnDefinition[];
  } catch {
    return [...DEFAULT_COLUMNS];
  }
}

function saveColumnsToLocalStorage(storageKey: string, columns: ColumnDefinition[]): void {
  localStorage.setItem(storageKey, JSON.stringify(columns));
}

function createColumnsStoreInstance(storageKey: string) {
  const initialColumns = loadColumnsFromLocalStorage(storageKey);

  const store = createStore({
    context: {
      columns: initialColumns,
    } as ColumnsContext,

    on: {
      'column.add': (ctx, event: { column: string; defaultCategoryIds?: string[] }) => {
        if (ctx.columns.some(c => c.name === event.column)) {
          console.warn(`[columnsStore] Column "${event.column}" already exists`);
          return ctx;
        }
        return {
          ...ctx,
          columns: [...ctx.columns, { name: event.column, defaultCategoryIds: event.defaultCategoryIds ?? [] }],
        };
      },

      'column.update': (ctx, event: { oldName: string; newName?: string; defaultCategoryIds?: string[] }) => {
        const columnIndex = ctx.columns.findIndex(c => c.name === event.oldName);
        if (columnIndex === -1) {
          console.warn(`[columnsStore] Column "${event.oldName}" not found`);
          return ctx;
        }
        const newName = event.newName ?? event.oldName;
        if (event.oldName !== newName && ctx.columns.some(c => c.name === newName)) {
          console.warn(`[columnsStore] Column "${newName}" already exists`);
          return ctx;
        }

        const updatedColumns = [...ctx.columns];
        updatedColumns[columnIndex] = {
          ...updatedColumns[columnIndex],
          name: newName,
          ...(event.defaultCategoryIds !== undefined && { defaultCategoryIds: event.defaultCategoryIds }),
        };

        return {
          ...ctx,
          columns: updatedColumns,
        };
      },

      'column.delete': (ctx, event: { column: string }) => {
        const columnIndex = ctx.columns.findIndex(c => c.name === event.column);
        if (columnIndex === -1) {
          console.warn(`[columnsStore] Column "${event.column}" not found`);
          return ctx;
        }
        return {
          ...ctx,
          columns: ctx.columns.filter(c => c.name !== event.column),
        };
      },

      'column.reorder': (ctx, event: { columns: ColumnDefinition[] }) => {
        return {
          ...ctx,
          columns: event.columns,
        };
      },

      'columns.reset': () => {
        return {
          columns: [...DEFAULT_COLUMNS],
        };
      },

      'columns.import': (_ctx, event: { columns: string[] | ColumnDefinition[] }) => {
        // Handle both legacy string[] and new ColumnDefinition[]
        if (event.columns.length > 0 && typeof event.columns[0] === 'string') {
          return {
            columns: (event.columns as string[]).map(name => ({ name, defaultCategoryIds: [] })),
          };
        }
        return {
          columns: event.columns as ColumnDefinition[],
        };
      },
    },
  });

  // Debounced persistence
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastColumnsHash = '';

  function computeColumnsHash(columns: ColumnDefinition[]): string {
    return columns.map(c => `${c.name}:${c.defaultCategoryIds.join(',')}`).join('|');
  }

  store.subscribe((snapshot) => {
    const currentHash = computeColumnsHash(snapshot.context.columns);
    if (currentHash === lastColumnsHash) return;
    lastColumnsHash = currentHash;

    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      saveColumnsToLocalStorage(storageKey, snapshot.context.columns);
    }, PERSISTENCE_DEBOUNCE_MS);
  });

  return store;
}

// ============================================
// Board-specific Store Instances
// ============================================

type ColumnsStoreInstance = ReturnType<typeof createColumnsStoreInstance>;

const storeInstances = new Map<string, ColumnsStoreInstance>();

export function initBoardColumnsStore(boardId: string): ColumnsStoreInstance {
  const existing = storeInstances.get(boardId);
  if (existing) return existing;

  const storageKey = getLocalStorageKey(boardId);
  const store = createColumnsStoreInstance(storageKey);
  storeInstances.set(boardId, store);
  return store;
}

export function getBoardColumnsStore(boardId: string): ColumnsStoreInstance {
  const instance = storeInstances.get(boardId);
  if (!instance) {
    throw new Error(`Columns store for board "${boardId}" not initialized. Call initBoardColumnsStore first.`);
  }
  return instance;
}

export function disposeBoardColumnsStore(boardId: string): void {
  storeInstances.delete(boardId);
}

// ============================================
// Helper-Funktionen
// ============================================

export function getColumnsArray(boardId: string): ColumnDefinition[] {
  return getBoardColumnsStore(boardId).getSnapshot().context.columns;
}

export function getColumnNames(boardId: string): string[] {
  return getColumnsArray(boardId).map(c => c.name);
}

export function columnExists(column: string, boardId: string): boolean {
  return getColumnsArray(boardId).some(c => c.name === column);
}

export type ColumnsStore = ColumnsStoreInstance;
